import { call, put, select, takeEvery } from 'redux-saga/effects';
import {
  getSelectedZusatzoptionen,
  GlobalActions,
  GlobalPut,
  VhvCheckoutProduktPayload,
  VorversicherungsItemVersicherteGefahr,
} from '@ps20-frontend/shop-integration-redux';
import {
  App,
  AUSLANDS_RISIKOORT,
  Auslandsrisikoort,
  isAuslandsrisikoort,
  Produktlinie,
} from '@ps20-frontend/frontend-models';
import { getSelectedProduktlinie } from '@ps20-frontend/preis-lib';
import {
  Counter,
  Feature,
  PrometheusCounterIncreasePayload,
  VorschadenArt,
} from '@ps20/bff-models';
import { selectFeatureToggle } from '@ps20-frontend/selector';
import { safeSaga } from '@ps20-frontend/safe-saga';
import { UwContext } from '@ps20-frontend/uw';
import {
  isVersicherungssumeGreaterThan500000,
  isVersicherungssumeLowerOrEqual500000,
  isWertsachenValueDefault,
  isWertsachenValueGreaterThan100000,
} from '@ps20-frontend/utils';
import { IAppState } from '../appState';
import { VhvActions } from '../vhvActions';
import { ZusatzoptionenNameVHVEnum } from '../model/zusatzoptionenVHV.model';
import { selectStaendigBewohntBoolean } from '../../app/shared/selectors/versObjekt.selector';
import { selectExtremwetterschutzActiveValue } from '../../app/shared/selectors/elementar-selbstbehalt.selector';
import { selectEntschaedigungsgrenzeRadOfSelectedLinie } from '../../app/shared/selectors/versicherungssummen.selector';
import {
  selectExtremwetterRisk,
  selectIsExtremwetterUWProcessNecessary,
  selectTragerisiko,
} from '../../app/shared/selectors/produkt.selector';
import { TragerisikoWerte } from '../model/tragerisiko.model';

const ENTSCHAEDIGUNGSGRENZE_TRAGERISIKO_UNIT_FACTOR = 1000;

export function buildTragerisikoPayload(tragerisiko: TragerisikoWerte): TragerisikoWerte | undefined {
  if (tragerisiko == null) {
    return undefined;
  }
  return {
    entschaedigungsgrenze:
      tragerisiko.entschaedigungsgrenze != null
        ? tragerisiko.entschaedigungsgrenze * ENTSCHAEDIGUNGSGRENZE_TRAGERISIKO_UNIT_FACTOR
        : null,
    einzelwert: tragerisiko.einzelwert,
  };
}

export const WERTSACHEN_SUMME_LIMIT = 30000;
const MIN_WERTSACHEN_VALUE_TO_SHOW_QUESTION_FT_ACTIVE = 50000;
const MAX_VERSICHERUNGSSUMME_VALUE_TO_SHOW_QUESTION_FT_ACTIVE = 500000;

export function* watchInDashboardUebernehmenSaga() {
  yield takeEvery(VhvActions.ABSPRUNG_INS_DASHBOARD, safeSaga(generateDashboardData, App.VHV_V2));
  yield takeEvery(VhvActions.ABSPRUNG_INS_DASHBOARD, safeSaga(generateProduktData, App.VHV_V2));
}

export function shouldShowAngabenZumHaushalt(state: IAppState): boolean {
  if (state.vertragsoptionen?.beibehalten) {
    return false;
  }
  const wertsachenValue = getWertsachenValue(state);
  const staendigBewohnt = selectStaendigBewohntBoolean(state);
  const versicherungssumme = state.vertragsoptionen.versicherungssumme;
  if (staendigBewohnt) {
    return (
      wertsachenValue > MIN_WERTSACHEN_VALUE_TO_SHOW_QUESTION_FT_ACTIVE ||
      versicherungssumme > 500000
    );
  } else {
    return versicherungssumme > 50000;
  }
}

function getWertsachenValue(state: IAppState) {
  const selectedProductLine = getSelectedProduktlinie(state.produkt);
  return (
    state.selectedWertsachensumme?.summe ||
    state.wertsachensummen.find(
      wertsachensumme => wertsachensumme.produktline === selectedProductLine.produktName,
    )?.summe
  );
}

function getVersicherungssumme(state: IAppState): number {
  const selectedProductLine = getSelectedProduktlinie(state.produkt);
  return state.versicherungssummen.find(
    summe => summe.produktlinie === selectedProductLine.produktName,
  )?.summe;
}

function shouldShowWertsachensummeQuestion(
  state: IAppState,
  wertsachenValue: number,
  versicherungssumme: number,
) {
  return (
    selectStaendigBewohntBoolean(state) &&
    wertsachenValue > MIN_WERTSACHEN_VALUE_TO_SHOW_QUESTION_FT_ACTIVE &&
    versicherungssumme <= MAX_VERSICHERUNGSSUMME_VALUE_TO_SHOW_QUESTION_FT_ACTIVE
  );
}

export function getGefahrenVorversicherung(
  state: IAppState,
): Array<VorversicherungsItemVersicherteGefahr> {
  const selectedProductLine = getSelectedProduktlinie(state.produkt);
  const elementarZusatzoption = selectedProductLine?.zusatzoptionen.find(
    zusatzoption => zusatzoption.name === VorschadenArt.ELEMENTAR,
  );
  if (elementarZusatzoption?.isSelected && state.vorschaeden?.elementarVisibleInErsatzgeschaeft) {
    return [
      {
        label: 'Weitere Naturgefahren',
        value: VorschadenArt.ELEMENTAR,
        selected: true,
      } as VorversicherungsItemVersicherteGefahr,
    ];
  }
  return [];
}

const globalActions = new GlobalActions();

export function* generateProduktData() {
  const globalPut = GlobalPut.getInstance();
  const state: IAppState = yield select();
  const selectedProductLine = getSelectedProduktlinie(state.produkt);
  const elementarZusatzoption = selectedProductLine?.zusatzoptionen.find(
    zusatzoption => zusatzoption.name === VorschadenArt.ELEMENTAR,
  );
  const countersIncreases: Array<PrometheusCounterIncreasePayload> = [];
  if (elementarZusatzoption && !elementarZusatzoption.disabled) {
    countersIncreases.push({
      counter: Counter.VHV_ELEMENTAR_VORBELEGT,
      increment: 1,
    });
    if (!elementarZusatzoption.isSelected) {
      countersIncreases.push({
        counter: Counter.VHV_ELEMENTAR_ABGEWAEHLT,
        increment: 1,
      });
    }
  }

  const wertsachenValue = getWertsachenValue(state);
  const versicherungssumme = getVersicherungssumme(state);

  function checkIfShouldSetHasWertsachen(
    state: IAppState,
    versicherungssumme: number,
    wertsachenValue: number,
    staendigBewohnt: boolean,
  ) {
    return (
      ((staendigBewohnt &&
        isVersicherungssumeLowerOrEqual500000(versicherungssumme) &&
        isWertsachenValueGreaterThan100000(wertsachenValue) &&
        !isWertsachenValueDefault(wertsachenValue, versicherungssumme)) ||
        (isVersicherungssumeGreaterThan500000(versicherungssumme) && staendigBewohnt))
    );
  }

  const isVhv2xActive = Boolean(selectFeatureToggle(state, Feature.VHV2X));

  const isTragerisikoSelected =
    selectedProductLine.zusatzoptionen.find(zusatzoption => zusatzoption.id === 'tragerisiko')
      ?.isSelected ?? false;

  const productActionPayload: VhvCheckoutProduktPayload = {
    appId: App.VHV_V2,
    angebotsId: state.angebotsId,
    produktlinie: isVhv2xActive ? selectedProductLine.produktName : undefined,
    laufzeit: state.vertragsoptionen.vertragslaufzeit,
    uebersichtTitle: 'Hausratversicherung',
    versichertesRisiko: `${state.risikoort.strasse} ${state.risikoort.hausnummer}, ${state.risikoort.plz} ${state.risikoort.ort}`,
    geokoordinatenDetails: state.risikoort.noneValidated
      ? `Längengrad: ${state.risikoort.longtitude}, Breitengrad: ${state.risikoort.latitude}`
      : undefined,
    showAngabenZumHaushalt: shouldShowAngabenZumHaushalt(state),
    vorvertragsnummer: state.vertragsoptionen.vorvertragsNummern,
    vorvertragSofortschutz: state.vertragsoptionen.vorvertragSofortschutz,
    countersIncreases: countersIncreases,
    extremwetterschutzBeratunghinweis: shouldExtremwetterschutzBeratungBeDisplayed(state),
    beginndatum: state.vertragsoptionen.beginnDatum,
    isImmobilieImAusland: state.risikoort.isImmobilieImAusland,
    wertsachenValue: wertsachenValue,
    versicherungssumme: versicherungssumme,
    showWertsachensummeQuestion:
      shouldShowWertsachensummeQuestion(state, wertsachenValue, versicherungssumme) &&
      !isTragerisikoSelected,
    staendigBewohnt: state.versObjekt.staendigBewohnt,
    stockwerk: state.versObjekt.stockwerk,
    hausart: state.versObjekt.hausart,
    hasWertsachen: checkIfShouldSetHasWertsachen(
      state,
      versicherungssumme,
      wertsachenValue,
      state.versObjekt.staendigBewohnt,
    ),
    angebotsdatum: state.vertragsoptionen.angebotsdatum,
    isTragerisikoSelected: isTragerisikoSelected,
    extremwetterschutzSelbstbeteiligung: selectExtremwetterschutzActiveValue(state),
    zuersZone: state.elementarSelbstbehalt?.id,
    entschaedigungsbetragRad: selectEntschaedigungsgrenzeRadOfSelectedLinie(state)?.betrag,
    tragerisiko: buildTragerisikoPayload(selectTragerisiko(state)),
  };

  const gefahrenVorversicherung = getGefahrenVorversicherung(state);
  if (gefahrenVorversicherung.length > 0) {
    productActionPayload.gefahrenVorversicherung = gefahrenVorversicherung;
  }

  yield call([globalPut, globalPut.put], globalActions.produktAction(productActionPayload));
}

export function shouldExtremwetterschutzBeratungBeDisplayed(state: IAppState): boolean {
  const produkt = state.produkt?.produktlinien[0]?.zusatzoptionen.find(
    zusatzoption => zusatzoption.name === ZusatzoptionenNameVHVEnum.ELEMENTAR,
  );
  return Boolean(produkt) && !produkt.disabled && !produkt.isSelected;
}

export function* generateDashboardData() {
  const state: IAppState = yield select();
  const globalPut = GlobalPut.getInstance();
  const selectedProduktlinie: Produktlinie = getSelectedProduktlinie(state.produkt);

  yield put(new VhvActions().vorschlagdatenChanged(state.angebotsId));

  yield call(
    [globalPut, globalPut.put],
    globalActions.inDashboardUebernehmen({
      appId: App.VHV_V2,
      angebotsId: state.angebotsId,
      kurztext: selectedProduktlinie.kurztext,
      ausgewaehlteLinie: selectedProduktlinie.produktName,
      versicherterRisikoort: state.risikoort,
      versichertesRisiko: getVersichertesRisikoDashboardAnzeige(
        state.risikoort,
        state.versObjekt.wohnflaeche,
      ),
      vertragslaufzeit: state.vertragsoptionen.vertragslaufzeit,
      selectZusatzoptionen: getSelectedZusatzoptionen(selectedProduktlinie.zusatzoptionen),
      beginn: state.vertragsoptionen.beginnDatum,
      hasVorschaeden: state.vorschaeden?.elementar > 0,
      extremwetterRisk: selectExtremwetterRisk(state),
    }),
  );

  yield call(
    [globalPut, globalPut.put],
    globalActions.setUnderwriterInfo(state.angebotsId, {
      [UwContext.NATURAL_HAZARDS]: selectIsExtremwetterUWProcessNecessary(state),
    }),
  );
}

export function getVersichertesRisikoDashboardAnzeige(
  risikoort: Auslandsrisikoort,
  wohnflaeche: number,
) {
  if (isAuslandsrisikoort(risikoort)) {
    return AUSLANDS_RISIKOORT;
  } else {
    const risikoortString = `${risikoort.strasse} ${risikoort.hausnummer}, ${risikoort.plz} ${risikoort.ort}, `;
    const wohnflaecheString = `${wohnflaeche} m²`;
    return `${risikoortString}${wohnflaecheString}`;
  }
}
